import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import investorStrategyService, {
  getSignedDriftedWeight,
  getSignedWeight,
  type InvestorStrategyRecord,
} from "../services/investorStrategyService";
import { productSearchService } from "../services/productSearchService";
import type { AlphaAdoptMixEntry } from "../pages/alpha/alphaAnalyzerAdopt";
import { useAuth } from "./AuthContext";
import {
  alphaAllocationDraftStorageKey,
  clearAlphaAllocationDraft,
} from "./alphaAllocationDraftStorage";
import type { ProductSearchItem } from "../types/productV3";
import {
  buildProductLookup,
  type ProductLookupEntry,
  type ProductLookupKey,
} from "../utils/strategyUtils";

// App-wide alpha allocation draft -- the alpha counterpart to StrategyDraftContext.
// Alpha owns this (rather than the legacy shared draft) so that Discover's
// add/remove-from-strategy toggles and the alpha allocation editor share ONE
// persistent, Managed-aware draft, and so alpha can stand alone at cut-over.
//
// Legacy safety: this provider is mounted app-wide but is INERT until a caller
// invokes __triggerInit (mirrors StrategyDraftContext.__triggerInit). Legacy
// screens never trigger it, so no fetch, no state, no behavior change for legacy.
// It also persists under its OWN sessionStorage key -- never the legacy one.

export type AlphaAllocationEntityType = "MANAGED_STRATEGY" | "PORTFOLIO" | "BASKET";

export interface AlphaAllocationRow {
  entityType: AlphaAllocationEntityType;
  productId: number;
  // Allocated weight the investor is setting. Long-only in Alpha, so >= 0.
  weight: number;
  // Live drifted weight (read-only). Null for a product not yet held.
  currentWeight: number | null;
  displayName: string;
}

export type AlphaAllocationsStatus = "idle" | "loading" | "loaded" | "error";

export const PLUTUS_MANAGED_LABEL = "Plutus Managed";

const PRODUCTS_PAGE_SIZE = 1000;
const PRODUCTS_FALLBACK_PAGE_SIZE = 100;

// Products removed from Alpha discovery until a proper gate exists. NOT a
// security control (reachable by URL); mirrors the analyzer's hidden list.
const HIDDEN_PRODUCTS: ReadonlyArray<{
  productType: string;
  providerId: number;
  productId: number;
}> = [{ productType: "PORTFOLIO", providerId: 9, productId: 4 }];

function isHiddenProduct(item: ProductSearchItem): boolean {
  return HIDDEN_PRODUCTS.some(
    (hidden) =>
      hidden.productType === item.productType
      && hidden.providerId === item.providerId
      && hidden.productId === item.productId
  );
}

function rowKey(entityType: string, productId: number): string {
  return `${entityType}-${productId}`;
}

function lookupKeyOf(
  entityType: "PORTFOLIO" | "BASKET",
  productId: number
): ProductLookupKey {
  return `${entityType}-${productId}` as ProductLookupKey;
}

function entriesEqual(a: AlphaAllocationRow[], b: AlphaAllocationRow[]): boolean {
  if (a.length !== b.length) return false;
  const map = new Map<string, number>();
  for (const e of a) map.set(rowKey(e.entityType, e.productId), e.weight);
  for (const e of b) {
    if (map.get(rowKey(e.entityType, e.productId)) !== e.weight) return false;
  }
  return true;
}

function persistRows(userId: string | null, rows: AlphaAllocationRow[]): void {
  if (!userId) return;
  try {
    sessionStorage.setItem(alphaAllocationDraftStorageKey(userId), JSON.stringify({ rows }));
  } catch {
    // quota exceeded or private mode -- ignore
  }
}

const ENTITY_TYPES: ReadonlyArray<AlphaAllocationEntityType> = [
  "MANAGED_STRATEGY",
  "PORTFOLIO",
  "BASKET",
];

// A persisted draft is untrusted input (hand-edited, or written by an older
// build). Drop anything malformed rather than seeding the editor with it.
function isValidStoredRow(value: unknown): value is AlphaAllocationRow {
  if (!value || typeof value !== "object") return false;
  const row = value as Partial<AlphaAllocationRow>;
  return ENTITY_TYPES.some((t) => t === row.entityType)
    && Number.isInteger(row.productId)
    && (row.productId as number) > 0
    && typeof row.weight === "number"
    && Number.isFinite(row.weight)
    && row.weight >= 0
    && typeof row.displayName === "string";
}

function readStoredRows(userId: string | null): AlphaAllocationRow[] | null {
  if (!userId) return null;
  const key = alphaAllocationDraftStorageKey(userId);
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.rows)) return null;
    if (!parsed.rows.every(isValidStoredRow)) {
      sessionStorage.removeItem(key);
      return null;
    }
    return parsed.rows as AlphaAllocationRow[];
  } catch {
    sessionStorage.removeItem(key);
    return null;
  }
}

const clearStoredRows = clearAlphaAllocationDraft;

// Mirrors StrategyDraftContext.fetchAllProducts: one large page, fall back to a
// smaller page if the backend rejects the size, then page through the rest.
async function fetchProductItems(): Promise<ProductSearchItem[]> {
  const fetchPage = async (pageSize: number): Promise<ProductSearchItem[]> => {
    const first = await productSearchService.searchProducts({ pageSize });
    let items = first.items;
    if (first.totalPages > 1) {
      const pages = await Promise.all(
        Array.from({ length: first.totalPages - 1 }, (_, index) =>
          productSearchService.searchProducts({ page: index + 2, pageSize })
        )
      );
      for (const page of pages) items = items.concat(page.items);
    }
    return items;
  };

  try {
    return await fetchPage(PRODUCTS_PAGE_SIZE);
  } catch (err) {
    if (err && typeof err === "object" && (err as { status?: number }).status === 400) {
      return fetchPage(PRODUCTS_FALLBACK_PAGE_SIZE);
    }
    throw err;
  }
}

function buildRows(
  records: InvestorStrategyRecord[],
  lookup: ReadonlyMap<ProductLookupKey, ProductLookupEntry>
): AlphaAllocationRow[] {
  return records.map((record) => {
    const isManaged = record.entityType === "MANAGED_STRATEGY";
    const title = isManaged
      ? PLUTUS_MANAGED_LABEL
      : lookup.get(lookupKeyOf(record.entityType, record.productId))?.title
        ?? `${record.entityType} #${record.productId}`;
    const drifted = getSignedDriftedWeight(record);
    return {
      entityType: record.entityType,
      productId: record.productId,
      // Editing sets a target, so seed from the allocated weight (not drift).
      // Alpha is long-only; clamp any historical short to 0.
      weight: Math.max(0, getSignedWeight(record)),
      currentWeight: drifted == null ? null : Math.max(0, drifted),
      displayName: title,
    };
  });
}

export interface AlphaAllocationDraft {
  status: AlphaAllocationsStatus;
  rows: AlphaAllocationRow[];
  savedRows: AlphaAllocationRow[];
  totalWeight: number;
  /** Saveable difference from the saved allocation (zero-weight rows excluded). */
  isDirty: boolean;
  /** Any pending difference, including a product added at 0% from Discover. */
  hasPendingChanges: boolean;
  minNlvOf: (entityType: string, productId: number) => number | null;
  addRow: (entry: Omit<AlphaAllocationRow, "weight" | "currentWeight">) => void;
  removeRow: (entityType: string, productId: number) => void;
  setWeight: (entityType: string, productId: number, weight: number) => void;
  /**
   * Adopt a What-If mix as the working draft. Leftover portfolios/baskets stay
   * at weight 0 so removals stay visible. An incoming managed row replaces any
   * other managed row (Plutus Managed is one slot). Spec
   * alpha-analyzer-adopt-to-allocations-2026-08-19, plus the singleton replace.
   */
  adoptMix: (entries: AlphaAdoptMixEntry[]) => void;
  /** Swap the Managed row's product in place, preserving its edited weight. */
  swapManagedProduct: (fromProductId: number, toProductId: number) => void;
  isInDraft: (entityType: string, productId: number) => boolean;
  resetToSaved: () => void;
  reload: () => void;
  /** Adopt a freshly-saved strategy as the new baseline (call after a successful apply). */
  markSaved: (records: InvestorStrategyRecord[]) => void;
  /** Drop the persisted draft and force the next trigger to reload from the server. */
  invalidate: () => void;
  catalog: ProductSearchItem[];
  productItems: ProductSearchItem[];
  basketLookup: Map<number, ProductSearchItem>;
  productLookup: Map<ProductLookupKey, ProductLookupEntry>;
}

type AlphaAllocationDraftContextValue = AlphaAllocationDraft & {
  __triggerInit: () => void;
};

const AlphaAllocationDraftContext =
  createContext<AlphaAllocationDraftContextValue | null>(null);

export const AlphaAllocationDraftProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [status, setStatus] = useState<AlphaAllocationsStatus>("idle");
  const [rows, setRows] = useState<AlphaAllocationRow[]>([]);
  const [savedRows, setSavedRows] = useState<AlphaAllocationRow[]>([]);
  const [lookup, setLookup] = useState<Map<ProductLookupKey, ProductLookupEntry>>(new Map());
  const [catalog, setCatalog] = useState<ProductSearchItem[]>([]);
  const [productItems, setProductItems] = useState<ProductSearchItem[]>([]);
  const { user, effectiveUserId } = useAuth();
  const userId = effectiveUserId === undefined
    ? user?.id ?? null
    : effectiveUserId;
  const requestRef = useRef(0);
  const initRef = useRef(false);
  // Persist only once a load has succeeded. Before that, savedRows is empty, so
  // storing a draft would restore a working draft that omits the investor's real
  // allocations -- applying it could drop them.
  const isLoadedRef = useRef(false);
  const userIdRef = useRef(userId);

  // A different effective account is selected: drop the in-memory draft and
  // re-arm the load so nothing carries across account owners.
  useLayoutEffect(() => {
    if (userIdRef.current === userId) return;
    userIdRef.current = userId;
    initRef.current = false;
    isLoadedRef.current = false;
    requestRef.current += 1;
    setRows([]);
    setSavedRows([]);
    setStatus("idle");
  }, [userId]);

  const load = useCallback(async () => {
    // Monotonic token: a slower earlier load must never overwrite a newer one.
    const requestId = ++requestRef.current;
    setStatus("loading");
    try {
      const [records, items] = await Promise.all([
        investorStrategyService.getStrategyRecords(),
        fetchProductItems(),
      ]);
      if (requestId !== requestRef.current) return;
      const productLookup = buildProductLookup(items);
      const built = buildRows(records, productLookup);
      setLookup(productLookup);
      setProductItems(items);
      setCatalog(
        items
          .filter((item) => !isHiddenProduct(item))
          // Portfolios lead the catalog; baskets follow. Alphabetical within each.
          .sort((a, b) => {
            if (a.productType !== b.productType) {
              return a.productType === "PORTFOLIO" ? -1 : 1;
            }
            return a.title.localeCompare(b.title);
          })
      );
      setSavedRows(built);
      // Restore an in-progress draft (e.g. products toggled on Discover), else
      // start the working draft from the saved allocations. A restored row's
      // drifted weight is a read-only live figure, so take it from the records
      // just fetched rather than the (older) persisted snapshot.
      const stored = readStoredRows(userIdRef.current);
      const currentByKey = new Map(
        built.map((row) => [rowKey(row.entityType, row.productId), row.currentWeight])
      );
      setRows(stored
        ? stored.map((row) => ({
          ...row,
          currentWeight: currentByKey.get(rowKey(row.entityType, row.productId)) ?? null,
        }))
        : built.map((row) => ({ ...row })));
      isLoadedRef.current = true;
      setStatus("loaded");
    } catch {
      if (requestId !== requestRef.current) return;
      setStatus("error");
    }
  }, []);

  const triggerInit = useCallback(() => {
    if (initRef.current) return;
    initRef.current = true;
    void load();
  }, [load]);

  const reload = useCallback(() => {
    initRef.current = true;
    void load();
  }, [load]);

  const addRow = useCallback((entry: Omit<AlphaAllocationRow, "weight" | "currentWeight">) => {
    setRows((prev) => {
      if (prev.some((e) => e.entityType === entry.entityType && e.productId === entry.productId)) {
        return prev;
      }
      return [...prev, { ...entry, weight: 0, currentWeight: null }];
    });
  }, []);

  const removeRow = useCallback((entityType: string, productId: number) => {
    setRows((prev) => prev.filter(
      (e) => !(e.entityType === entityType && e.productId === productId)
    ));
  }, []);

  const setWeight = useCallback((entityType: string, productId: number, weight: number) => {
    const next = Number.isFinite(weight) && weight > 0 ? weight : 0;
    setRows((prev) => prev.map(
      (e) => (e.entityType === entityType && e.productId === productId ? { ...e, weight: next } : e)
    ));
  }, []);

  // Adopt a What-If mix from the analyzer: "your allocation becomes this
  // strategy". One atomic transform. Leftover portfolios/baskets stay at
  // weight 0 so their removal is visible and the full-control save cannot
  // drop them invisibly. Plutus Managed is one slot: an incoming managed
  // row replaces any other managed row instead of leaving it at 0% (that
  // leftover tripped the multiple-managed edit lock). Mix entries upsert
  // their weights (an existing row keeps its live currentWeight). Names for
  // new rows come from the entry, else the catalog lookup, else a typed
  // placeholder.
  const adoptMix = useCallback((entries: AlphaAdoptMixEntry[]) => {
    setRows((prev) => {
      const mixByKey = new Map(
        entries.map((entry) => [rowKey(entry.entityType, entry.productId), entry])
      );
      const incomingHasManaged = entries.some(
        (entry) => entry.entityType === "MANAGED_STRATEGY"
      );
      const next: AlphaAllocationRow[] = [];
      for (const row of prev) {
        const key = rowKey(row.entityType, row.productId);
        const match = mixByKey.get(key);
        if (match) {
          mixByKey.delete(key);
          next.push({ ...row, weight: Math.max(0, match.weight) });
          continue;
        }
        if (incomingHasManaged && row.entityType === "MANAGED_STRATEGY") {
          continue;
        }
        next.push({ ...row, weight: 0 });
      }
      for (const entry of mixByKey.values()) {
        next.push({
          entityType: entry.entityType,
          productId: entry.productId,
          weight: Math.max(0, entry.weight),
          currentWeight: null,
          displayName: entry.displayName
            || (entry.entityType === "MANAGED_STRATEGY"
              ? PLUTUS_MANAGED_LABEL
              : lookup.get(lookupKeyOf(entry.entityType, entry.productId))?.title)
            || `${entry.entityType} #${entry.productId}`,
        });
      }
      return next;
    });
  }, [lookup]);

  // Swap the Managed row's product in place (a mix rematch triggered by a weight
  // edit). The edited weight is preserved; the drifted weight belongs to the
  // product it was fetched for, so it carries over only when swapping back to a
  // product present in the saved baseline. No-op when the source row is absent;
  // if a row with the target id is already drafted, the source row folds into it.
  const swapManagedProduct = useCallback((fromProductId: number, toProductId: number) => {
    if (fromProductId === toProductId) return;
    setRows((prev) => {
      const fromIndex = prev.findIndex(
        (e) => e.entityType === "MANAGED_STRATEGY" && e.productId === fromProductId
      );
      if (fromIndex === -1) return prev;
      const from = prev[fromIndex];
      const hasTarget = prev.some(
        (e) => e.entityType === "MANAGED_STRATEGY" && e.productId === toProductId
      );
      if (hasTarget) {
        return prev
          .filter((_, index) => index !== fromIndex)
          .map((e) => (
            e.entityType === "MANAGED_STRATEGY" && e.productId === toProductId
              ? { ...e, weight: from.weight }
              : e
          ));
      }
      const savedTarget = savedRows.find(
        (e) => e.entityType === "MANAGED_STRATEGY" && e.productId === toProductId
      );
      return prev.map((e, index) => (
        index === fromIndex
          ? { ...e, productId: toProductId, currentWeight: savedTarget?.currentWeight ?? null }
          : e
      ));
    });
  }, [savedRows]);

  const isInDraft = useCallback(
    (entityType: string, productId: number) => rows.some(
      (e) => e.entityType === entityType && e.productId === productId
    ),
    [rows]
  );

  const resetToSaved = useCallback(() => {
    setRows(savedRows.map((row) => ({ ...row })));
  }, [savedRows]);

  const markSaved = useCallback((records: InvestorStrategyRecord[]) => {
    if (userIdRef.current !== userId) return;
    const built = buildRows(records, lookup);
    setSavedRows(built);
    setRows(built.map((row) => ({ ...row })));
  }, [lookup, userId]);

  // Drop the persisted draft and force the next trigger to reload from the
  // server. Used when the post-save read-back fails: without this the stale
  // baseline (and the pre-save stored draft, which load() prefers over fresh
  // rows) would describe an account state that no longer exists.
  const invalidate = useCallback(() => {
    if (userIdRef.current !== userId) return;
    clearStoredRows(userId);
    initRef.current = false;
    isLoadedRef.current = false;
    requestRef.current += 1;
    setStatus("idle");
  }, [userId]);

  const minNlvOf = useCallback(
    (entityType: string, productId: number): number | null => {
      if (entityType !== "PORTFOLIO" && entityType !== "BASKET") return null;
      return lookup.get(lookupKeyOf(entityType, productId))?.minNlv ?? null;
    },
    [lookup]
  );

  const totalWeight = rows.reduce((sum, e) => sum + e.weight, 0);
  // Saveable difference: compare like with like. Filtering only the working side
  // would leave an account whose saved set contains a zero-weight row (a legacy
  // short clamped to 0) permanently dirty with nothing the investor can change.
  // Zero-weight rows are excluded because the save payload drops them, so such a
  // row can never be saved away -- counting it would mean permanent dirtiness.
  const isDirty = !entriesEqual(
    rows.filter((e) => e.weight > 0),
    savedRows.filter((e) => e.weight > 0)
  );
  // Anything pending at all, including a product added at 0% from Discover.
  // Drives what we persist and what Discard offers to throw away -- neither of
  // which should ignore an addition just because it has no weight yet.
  const hasPendingChanges = !entriesEqual(rows, savedRows);

  // Persist as an effect, not inside a state updater: an updater must stay pure
  // (React may discard and re-run a render pass). Storage tracks the pending
  // draft, so a draft equal to saved clears it instead.
  useEffect(() => {
    if (!isLoadedRef.current || !userId) return;
    if (hasPendingChanges) persistRows(userId, rows);
    else clearStoredRows(userId);
  }, [rows, hasPendingChanges, userId]);

  const basketLookup = useMemo(() => {
    const map = new Map<number, ProductSearchItem>();
    for (const item of productItems) {
      if (item.productType === "BASKET") map.set(item.productId, item);
    }
    return map;
  }, [productItems]);

  const value = useMemo<AlphaAllocationDraftContextValue>(() => ({
    status,
    rows,
    savedRows,
    totalWeight,
    isDirty,
    hasPendingChanges,
    minNlvOf,
    addRow,
    removeRow,
    setWeight,
    adoptMix,
    swapManagedProduct,
    isInDraft,
    resetToSaved,
    reload,
    markSaved,
    invalidate,
    catalog,
    productItems,
    basketLookup,
    productLookup: lookup,
    __triggerInit: triggerInit,
  }), [
    status, rows, savedRows, totalWeight, isDirty, hasPendingChanges, minNlvOf,
    addRow, removeRow, setWeight, adoptMix, swapManagedProduct, isInDraft,
    resetToSaved, reload, markSaved, invalidate, catalog, productItems,
    basketLookup, lookup, triggerInit,
  ]);

  return (
    <AlphaAllocationDraftContext.Provider value={value}>
      {children}
    </AlphaAllocationDraftContext.Provider>
  );
};

// Raw context reader. Does NOT trigger a load -- callers decide when to init, so
// legacy surfaces can read the (idle) context without causing a fetch.
export function useAlphaAllocationDraftContext(): AlphaAllocationDraftContextValue {
  const ctx = useContext(AlphaAllocationDraftContext);
  if (!ctx) {
    throw new Error(
      "useAlphaAllocationDraft must be used within an AlphaAllocationDraftProvider"
    );
  }
  return ctx;
}

// Alpha-surface hook: reads the draft AND triggers a one-time load on mount.
export function useAlphaAllocationDraft(): AlphaAllocationDraft {
  const ctx = useAlphaAllocationDraftContext();
  const { __triggerInit } = ctx;
  useEffect(() => {
    __triggerInit();
  }, [__triggerInit]);
  return ctx;
}
